import { Common } from './common';
import {
  CsvBulkUpdateValues,
  EhrProviderInformation,
  FindAllOptions,
  Provider,
  ProviderCsv,
  ProviderWithEnabledPractices,
  UpsertValues,
} from './models/provider';
import { User, UserBasic } from './models/user';
import { ImportValidation } from './models/provider';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(
    query: FindAllOptions = {},
  ): Promise<ProviderWithEnabledPractices[]> {
    const url = this.common.constructPath('/provider', query);
    const { providers } = await this.common.getJSON(url);
    return providers;
  }

  async findAllBasic(query: FindAllOptions = {}): Promise<UserBasic[]> {
    const url = this.common.constructPath('/provider/basic', query);
    const { providers } = await this.common.getJSON(url);
    return providers;
  }

  async findOneById(id: string): Promise<ProviderWithEnabledPractices> {
    const url = this.common.constructPath(`/provider/${id}`);
    const { provider } = await this.common.getJSON(url);
    return provider;
  }

  async upsert(values: UpsertValues): Promise<Provider> {
    const url = this.common.constructPath(`/provider`);
    return this.common.putJSON(url, values);
  }

  async update(id: string, values: { npi: string }): Promise<Provider> {
    const url = this.common.constructPath(`/provider/${id}`);
    return this.common.patchJSON(url, values);
  }

  async findOneByEhrProviderId(id: string): Promise<User> {
    const url = this.common.constructPath(
      `/ehr-provider-information/${id}/provider`,
    );
    return this.common.getJSON(url);
  }

  async findOneByEhrProviderValue(
    practiceId: string,
    value: string,
  ): Promise<{
    provider: User;
    ehrProviderInformation: EhrProviderInformation;
  }> {
    const url = this.common.constructPath(
      `/ehr-provider-information/provider`,
      {
        practiceId,
        value,
      },
    );
    return this.common.getJSON(url);
  }

  async createEhrProviderInformation(ehrProviderInformation: {
    practiceId: string;
    providerUserId: string;
    value: string;
  }): Promise<EhrProviderInformation> {
    const url = this.common.constructPath('/ehr-provider-information/');
    return this.common.postJSON(url, ehrProviderInformation);
  }

  async fetchReckoningMetrics(args: {
    providerUserId: string;
    unit?: string;
    startTime?: Date;
    endTime?: Date;
  }) {
    const url = this.common.constructPath(`/provider/reckoningMetrics`, args);
    return this.common.getJSON(url);
  }

  async fetchWordAccuracyMetrics(args: {
    providerUserId: string;
    unit?: string;
    startTime?: Date;
    endTime?: Date;
  }) {
    const url = this.common.constructPath(
      `/provider/fetchWordAccuracyMetrics`,
      args,
    );
    return this.common.getJSON(url);
  }

  async fetchReportMetrics(args: {
    providerUserId: string;
    unit?: string;
    startTime?: Date;
    endTime?: Date;
  }) {
    const url = this.common.constructPath(`/provider/reportMetrics`, args);
    return this.common.getJSON(url);
  }

  async fetchCommonPhrases(providerUserId: string): Promise<string[]> {
    const url = this.common.constructPath(
      `/provider/${providerUserId}/commonphrases`,
    );
    const { phrases } = await this.common.getJSON(url);
    return phrases;
  }

  async verifyImport(
    values: Record<string, string>[],
  ): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(`/provider/import/verify`);
    return this.common.postJSON(url, { providers: values });
  }

  async import(values: CsvBulkUpdateValues[]) {
    const url = this.common.constructPath(`/provider`);
    return this.common.patchJSON(url, { providers: values });
  }

  async csvVerify(
    values: Record<string, string>[],
  ): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(`/provider/csv/verify`);
    return this.common.postJSON(url, values);
  }

  async csvUpsert(values: ProviderCsv[]): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`/provider/csv/`);
    return this.common.putJSON(url, values);
  }
}
